import { assertCanModifyEmbodiment, type UserRole } from "@/app/userRole";
import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";
import { fetchAllPages, type PageQuery, type PageResp } from "./page";

export type EndEffector = {
  uuid: string;
  name: string;
  tag: string | null;
  description: string | null;
  created_at: string;
  modified_at: string;
};

// Tip 은 Dataset 의 tip 축이다(EE 끝단, EE 와 직교). GET /v1/tips 카탈로그로 조회한다.
export type Tip = {
  uuid: string;
  name: string;
  tag: string | null;
  description: string | null;
  created_at: string;
  modified_at: string;
};

// Embodiment 응답이다. 부품은 nested 로 들어 있다. name 이 유일 식별자다.
// 팔 구성은 이 카탈로그가 정하지 않는다. Dataset 이 실측 채널에서 판정한다(`Dataset.arm_layout`).
export type Embodiment = {
  uuid: string;
  name: string;
  description: string | null;
  end_effector: EndEffector;
  created_at: string;
  modified_at: string;
};

export type EmbodimentMutationOptions = {
  role: UserRole | null;
};

// ── 부품 생성 payload ──────────────────────────────────────────────────────

export type EndEffectorCreateReq = {
  name: string;
  tag?: string | null;
  description?: string | null;
};

export type EndEffectorUpdateReq = {
  name?: string | null;
  tag?: string | null;
  description?: string | null;
};

export type TipCreateReq = {
  name: string;
  tag?: string | null;
  description?: string | null;
};

export type TipUpdateReq = {
  name?: string | null;
  tag?: string | null;
  description?: string | null;
};

export type EmbodimentCreateReq = {
  end_effector_uuid: string;
  name: string;
  description?: string | null;
};

// 부분 수정 payload 다. BE 는 `model_dump(exclude_unset=True)` 로 **보낸 키만** 적용한다.
// 키가 없으면 "변경 없음"이다. 명시한 null 은 그 필드를 비우라는 뜻이 **아니다**.
// `end_effector_uuid: null` 을 보내면 BE 가 null 로 EndEffector 를 조회해 404
// `end_effector.not_found` 를 반환한다. 이 계약에는 연결을 해제하는 방법이 없다.
export type EmbodimentUpdateReq = {
  end_effector_uuid?: string | null;
  name?: string | null;
  description?: string | null;
};

// ── Embodiment ─────────────────────────────────────────────────────────────

export type EmbodimentListQuery = PageQuery & {
  // 서버가 name 에 case-insensitive contains 검색을 한다. 값이 없으면 전체 카탈로그를 반환한다.
  // 클라이언트가 카탈로그 전체를 받아 거르지 않도록, 서버가 1 round-trip 으로 필터한다.
  search?: string;
};

export async function listEmbodiments(
  query: EmbodimentListQuery = {},
  options: { signal?: AbortSignal } = {},
): Promise<PageResp<Embodiment>> {
  const response = await apiClient.get<PageResp<Embodiment>>(apiRoutes.embodiment.collection, {
    params: query,
    signal: options.signal,
  });
  return response.data;
}

export async function getEmbodiment(embodimentUuid: string): Promise<Embodiment> {
  const response = await apiClient.get<Embodiment>(apiRoutes.embodiment.detail(embodimentUuid));
  return response.data;
}

export async function createEmbodiment(
  payload: EmbodimentCreateReq,
  options: EmbodimentMutationOptions,
): Promise<Embodiment> {
  assertCanModifyEmbodiment(options.role);
  const response = await apiClient.post<Embodiment>(apiRoutes.embodiment.collection, payload);
  return response.data;
}

export async function updateEmbodiment(
  embodimentUuid: string,
  payload: EmbodimentUpdateReq,
  options: EmbodimentMutationOptions,
): Promise<Embodiment> {
  assertCanModifyEmbodiment(options.role);
  const response = await apiClient.patch<Embodiment>(
    apiRoutes.embodiment.detail(embodimentUuid),
    payload,
  );
  return response.data;
}

export async function deleteEmbodiment(
  embodimentUuid: string,
  options: EmbodimentMutationOptions,
): Promise<void> {
  assertCanModifyEmbodiment(options.role);
  await apiClient.delete(apiRoutes.embodiment.detail(embodimentUuid));
}

// ── EndEffector ────────────────────────────────────────────────────────────

export async function listEndEffectors(
  query: PageQuery = {},
  options: { signal?: AbortSignal } = {},
): Promise<PageResp<EndEffector>> {
  const response = await apiClient.get<PageResp<EndEffector>>(apiRoutes.endEffector.collection, {
    params: query,
    signal: options.signal,
  });
  return response.data;
}

export async function getEndEffector(endEffectorUuid: string): Promise<EndEffector> {
  const response = await apiClient.get<EndEffector>(apiRoutes.endEffector.detail(endEffectorUuid));
  return response.data;
}

export async function createEndEffector(
  payload: EndEffectorCreateReq,
  options: EmbodimentMutationOptions,
): Promise<EndEffector> {
  assertCanModifyEmbodiment(options.role);
  const response = await apiClient.post<EndEffector>(apiRoutes.endEffector.collection, payload);
  return response.data;
}

export async function updateEndEffector(
  endEffectorUuid: string,
  payload: EndEffectorUpdateReq,
  options: EmbodimentMutationOptions,
): Promise<EndEffector> {
  assertCanModifyEmbodiment(options.role);
  const response = await apiClient.patch<EndEffector>(
    apiRoutes.endEffector.detail(endEffectorUuid),
    payload,
  );
  return response.data;
}

export async function deleteEndEffector(
  endEffectorUuid: string,
  options: EmbodimentMutationOptions,
): Promise<void> {
  assertCanModifyEmbodiment(options.role);
  await apiClient.delete(apiRoutes.endEffector.detail(endEffectorUuid));
}

// ── Tip ──────────────────────────────────────────────────────────────────

export async function listTips(
  query: PageQuery = {},
  options: { signal?: AbortSignal } = {},
): Promise<PageResp<Tip>> {
  const response = await apiClient.get<PageResp<Tip>>(apiRoutes.tip.collection, {
    params: query,
    signal: options.signal,
  });
  return response.data;
}

export async function getTip(tipUuid: string): Promise<Tip> {
  const response = await apiClient.get<Tip>(apiRoutes.tip.detail(tipUuid));
  return response.data;
}

export async function createTip(
  payload: TipCreateReq,
  options: EmbodimentMutationOptions,
): Promise<Tip> {
  assertCanModifyEmbodiment(options.role);
  const response = await apiClient.post<Tip>(apiRoutes.tip.collection, payload);
  return response.data;
}

export async function updateTip(
  tipUuid: string,
  payload: TipUpdateReq,
  options: EmbodimentMutationOptions,
): Promise<Tip> {
  assertCanModifyEmbodiment(options.role);
  const response = await apiClient.patch<Tip>(apiRoutes.tip.detail(tipUuid), payload);
  return response.data;
}

export async function deleteTip(
  tipUuid: string,
  options: EmbodimentMutationOptions,
): Promise<void> {
  assertCanModifyEmbodiment(options.role);
  await apiClient.delete(apiRoutes.tip.detail(tipUuid));
}

// 카탈로그 전체를 가져온다. picker 드롭다운이 전체 목록을 한 번에 표시한다.
export function listAllEmbodiments(): Promise<Embodiment[]> {
  return fetchAllPages(listEmbodiments);
}

export function listAllEndEffectors(): Promise<EndEffector[]> {
  return fetchAllPages(listEndEffectors);
}

export function listAllTips(): Promise<Tip[]> {
  return fetchAllPages(listTips);
}
